import React, { useState, useEffect } from 'react';
import { logoService } from '../../services/logoService';
import LoadingSpinner from '../ui/LoadingSpinner';

interface LogoData {
  url: string;
  alt: string;
  width?: number;
  height?: number;
  updatedAt: string;
}

export const LogoManager: React.FC = () => {
  const [logo, setLogo] = useState<LogoData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadLogo();
  }, []);

  const loadLogo = async () => {
    try {
      const data = await logoService.getLogo();
      setLogo(data);
    } catch (err) {
      setError('Failed to load logo');
      console.error('Error loading logo:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setIsSaving(true);
      setError(null);

      // Upload the file to Firebase Storage
      await logoService.updateLogo(
        file,
        'Ridgeline Homes Logo',
        200, // Default width
        50   // Default height
      );

      await loadLogo();
    } catch (err) {
      setError('Failed to update logo');
      console.error('Error updating logo:', err);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-2xl font-semibold mb-6">Logo Management</h2>

      {error && (
        <div className="mb-4 p-4 bg-red-50 text-red-700 rounded-md">
          {error}
        </div>
      )}

      <div className="space-y-6">
        {/* Current Logo Display */}
        <div className="border rounded-lg p-4">
          <h3 className="text-lg font-medium mb-4">Current Logo</h3>
          {logo ? (
            <div className="flex items-center space-x-4">
              <img
                src={logo.url}
                alt={logo.alt}
                className="max-h-16 object-contain"
              />
              <div className="text-sm text-gray-500">
                <p>Last updated: {new Date(logo.updatedAt).toLocaleDateString()}</p>
                <p>Dimensions: {logo.width}x{logo.height}px</p>
              </div>
            </div>
          ) : (
            <p className="text-gray-500">No logo uploaded yet</p>
          )}
        </div>

        {/* Upload New Logo */}
        <div className="border rounded-lg p-4">
          <h3 className="text-lg font-medium mb-4">Upload New Logo</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-center w-full">
              <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100">
                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                  <svg className="w-8 h-8 mb-4 text-gray-500" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 16">
                    <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"/>
                  </svg>
                  <p className="mb-2 text-sm text-gray-500">
                    <span className="font-semibold">Click to upload</span> or drag and drop
                  </p>
                  <p className="text-xs text-gray-500">SVG, PNG, JPG or GIF (MAX. 800x400px)</p>
                </div>
                <input
                  type="file"
                  className="hidden"
                  accept="image/*"
                  onChange={handleFileChange}
                  disabled={isSaving}
                />
              </label>
            </div>
            {isSaving && (
              <div className="flex justify-center">
                <LoadingSpinner />
                <span className="ml-2 text-gray-500">Uploading logo...</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}; 